import { type } from 'arktype';
import type { TranslatableText } from '@zag/core/common';
import { translatableTextSchema } from '@zag/core/models';

export const BALANCE_TYPE_TRANSLATABLE_FIELDS = ['label'] as const;
export type BalanceTypeTranslatableField = (typeof BALANCE_TYPE_TRANSLATABLE_FIELDS)[number];
export type BalanceTypeTranslations = TranslatableText<BalanceTypeTranslatableField>;

/**
 * Carry-over rules for balance type definitions.
 */
export interface CarryOverRules {
  enabled?: boolean;
  maxCarryOver?: number;
  expiryMonths?: number;
}

/**
 * Accrual configuration for balance type definitions.
 */
export interface AccrualConfig {
  mode?: 'none' | 'annual' | 'monthly';
  rate?: number;
}

/**
 * Balance type definition response DTO.
 */
export interface BalanceTypeDefinition {
  id: string;
  key: string;
  label: string;
  icon: string;
  color: string;
  unit: string;
  defaultAnnualBudget: number | null;
  carryOverRules: CarryOverRules;
  accrualConfig: AccrualConfig;
  priority: number;
  isActive: boolean;
  isSystem: boolean;
  showInWeek: boolean;
  createdAt: string;
  updatedAt: string;
  translations?: TranslatableText<string>;
  thresholdGreenMin?: number | null;
  thresholdGreenMax?: number | null;
  thresholdAmberMin?: number | null;
  thresholdAmberMax?: number | null;
}

/**
 * Schema for creating a new balance type definition.
 */
const carryOverRulesSchema = type({
  'enabled?': type('boolean').configure({
    description: 'When true, unused balance at year-end carries over into the next year.',
  }),
  'maxCarryOver?': type('number').configure({
    description: 'Maximum amount (in the balance unit) that may be carried over.',
    examples: [5],
  }),
  'expiryMonths?': type('number').configure({
    description: 'Number of months after which carried-over balance expires.',
    examples: [3],
  }),
}).configure({ description: 'Carry-over rules applied at year-end for this balance type.' });

const accrualConfigSchema = type({
  'mode?': type("'none'|'annual'|'monthly'").configure({
    description: "Accrual cadence: 'none' (manual), 'annual' (granted once per year), or 'monthly' (accrued each month).",
    examples: ['annual'],
  }),
  'rate?': type('number').configure({
    description: 'Amount granted per accrual cycle, in the balance unit.',
    examples: [25],
  }),
}).configure({ description: 'Automatic accrual configuration for this balance type.' });

interface AmpelThresholdFields {
  thresholdGreenMin?: number | null;
  thresholdGreenMax?: number | null;
  thresholdAmberMin?: number | null;
  thresholdAmberMax?: number | null;
}

const AMPEL_THRESHOLDS_MESSAGE =
  'all four threshold fields to be omitted/null together, or all set with thresholdAmberMin <= thresholdGreenMin <= 0 <= thresholdGreenMax <= thresholdAmberMax';

/**
 * Mirrors the planned DB check constraint `balance_type_defs_thresholds_valid`
 * so invalid payloads fail fast with a 400 instead of hitting the DB constraint.
 */
function ampelThresholdsAreConsistent(fields: AmpelThresholdFields): boolean {
  const { thresholdGreenMin, thresholdGreenMax, thresholdAmberMin, thresholdAmberMax } = fields;
  if (thresholdGreenMin == null && thresholdGreenMax == null && thresholdAmberMin == null && thresholdAmberMax == null) {
    return true;
  }
  if (thresholdGreenMin == null || thresholdGreenMax == null || thresholdAmberMin == null || thresholdAmberMax == null) {
    return false;
  }
  return (
    thresholdAmberMin <= thresholdGreenMin && thresholdGreenMin <= 0 && 0 <= thresholdGreenMax && thresholdGreenMax <= thresholdAmberMax
  );
}

const ampelThresholdFieldsSchema = {
  'thresholdGreenMin?': type('number|null').configure({
    description: 'Lower bound of the green Ampel zone (<= 0). Must be set together with the other three thresholds, or all left null.',
    examples: [-2],
  }),
  'thresholdGreenMax?': type('number|null').configure({
    description: 'Upper bound of the green Ampel zone (>= 0).',
    examples: [3],
  }),
  'thresholdAmberMin?': type('number|null').configure({
    description: 'Lower bound of the amber Ampel zone; below this the balance is red.',
    examples: [-3.5],
  }),
  'thresholdAmberMax?': type('number|null').configure({
    description: 'Upper bound of the amber Ampel zone; above this the balance is red.',
    examples: [5],
  }),
};

export const createBalanceTypeDefinitionSchema = type({
  key: type('string').configure({
    description: 'Stable machine-readable identifier, unique per tenant.',
    examples: ['vacation'],
  }),
  label: type('string').configure({
    description: 'Human-readable label shown in the UI.',
    examples: ['Vacation'],
  }),
  'icon?': type('string').configure({
    description: 'Material Icons identifier used on balance chips.',
    examples: ['beach_access'],
  }),
  'color?': type('string').configure({
    description: 'CSS color used to accent the balance (hex recommended).',
    examples: ['#0ea5e9'],
  }),
  'unit?': type("'hours'|'days'").configure({
    description: "Balance unit: 'hours' for hour-based tracking, 'days' for day-based.",
    examples: ['days'],
  }),
  'defaultAnnualBudget?': type('number|null').configure({
    description: 'Default annual budget applied to new persons. `null` means no default budget.',
    examples: [25],
  }),
  'carryOverRules?': carryOverRulesSchema,
  'accrualConfig?': accrualConfigSchema,
  'priority?': type('number').configure({
    description: 'Sort order used in pickers. Lower numbers appear first.',
    examples: [10],
  }),
  'isActive?': type('boolean').configure({
    description: 'Whether the definition is offered in the UI. Defaults to `true` when omitted.',
  }),
  'showInWeek?': type('boolean').configure({
    description: 'When true, this balance appears as a saldo widget in the employee\'s "My Week" view. Defaults to `false`.',
  }),
  'translations?': translatableTextSchema(['label']),
  ...ampelThresholdFieldsSchema,
})
  .narrow((value, ctx) => ampelThresholdsAreConsistent(value) || ctx.mustBe(AMPEL_THRESHOLDS_MESSAGE))
  .configure({ description: 'Payload for creating a new balance type definition.' });
export type CreateBalanceTypeDefinition = typeof createBalanceTypeDefinitionSchema.t;

/**
 * Schema for updating a balance type definition.
 * System types have restricted editable fields.
 */
export const updateBalanceTypeDefinitionSchema = type({
  'label?': type('string').configure({ description: 'Updated display label.', examples: ['Vacation'] }),
  'icon?': type('string').configure({ description: 'Updated Material Icons identifier.', examples: ['beach_access'] }),
  'color?': type('string').configure({ description: 'Updated CSS color (hex recommended).', examples: ['#0ea5e9'] }),
  'unit?': type("'hours'|'days'").configure({
    description: "Updated balance unit: 'hours' or 'days'. Ignored for system types.",
  }),
  'defaultAnnualBudget?': type('number|null').configure({
    description: 'Updated default annual budget. `null` clears the default.',
    examples: [25],
  }),
  'carryOverRules?': carryOverRulesSchema,
  'accrualConfig?': accrualConfigSchema,
  'priority?': type('number').configure({ description: 'Updated sort priority.', examples: [10] }),
  'isActive?': type('boolean').configure({
    description: 'Activate (`true`) or deactivate (`false`) this definition.',
  }),
  'showInWeek?': type('boolean').configure({
    description: 'Show (`true`) or hide (`false`) this balance as a saldo widget in the employee\'s "My Week" view.',
  }),
  'translations?': translatableTextSchema(['label']),
  ...ampelThresholdFieldsSchema,
})
  .narrow((value, ctx) => ampelThresholdsAreConsistent(value) || ctx.mustBe(AMPEL_THRESHOLDS_MESSAGE))
  .configure({
    description:
      'Partial update for a balance type definition. System types only honour label, icon, color, priority, isActive, showInWeek, and thresholds.',
  });
export type UpdateBalanceTypeDefinition = typeof updateBalanceTypeDefinitionSchema.t;
